import { MyProductColumnGroupId } from './myProductColumnGroups'
import { MyProductColumnId, MyYandexMarketProductColumnId, selectColumnId } from './myProductColumnIds'
import { createColumnGroup, createColumns, GroupedMyColumnsParam, MyColumnParam } from './columnGroupCreator'

import { ProductImageCell2 } from '../components/ProductImageCell'
import { TableDateCell } from '../components/tableCells/TableDateCell'
import { TableSelectCell } from '../components/tableCells/TableSelectCell'
import { YandexMarketProductSyncLogsCell } from '../components/tableCells/StoreProductSyncLogsCell'
import { MyProductYandexMarketSyncCell } from '../components/tableCells/MyProductYandexMarketSyncCell'

import { tableCellStyles } from '../hooks/tableStylesHook'

import { emDash } from '../common/stringUtility'
import { formatDateOnly } from '../common/dateTimeUtility'
import { formatBalance, formatPrice, formatPriceOrEmpty } from '../common/numberUtils'

import { MyProductModel } from '../server/mpsklad_core/Models/MyProductModel'
import { YandexMarketAccountModel } from '../server/mpsklad_core/Models/YandexMarketAccountModel'
import { YandexMarketProductModel } from '../server/mpsklad_core/Models/YandexMarketProductModel'
import { YandexMarketPrintOrderModel } from '../server/mpsklad_core/Models/YandexMarketPrintOrderModel'
import { YandexMarketDeliveryType } from '../server/mpsklad_core/Entity/YandexMarket/Enums/YandexMarketDeliveryType'

export const myYandexMarketColumns =
  createColumnGroup<MyProductColumnId, MyProductModel>()(
    MyProductColumnGroupId.YandexMarket, [{
      title: 'Передача остатков',
      id: 'yandexMarketSyncEnabled',
      sortable: true,
      CellComponent: MyProductYandexMarketSyncCell
    }, {
      title: 'Ваш Sku',
      id: 'yandexMarketShopSku',
      sortable: true,
      accessor: _ => _.yandexMarketProducts.map(p => p.shopSku)
    }, {
      title: 'Артикул от производителя',
      id: 'yandexMarketVendorCode',
      accessor: _ => _.yandexMarketProducts.map(p => p.vendorCode)
    }, {
      title: 'Цена',
      id: 'yandexMarketPrice',
      accessor: _ => _.yandexMarketProducts.map(p => formatPriceOrEmpty(p.price)),
      cellStyle: tableCellStyles.noWrap
    }, {
      title: 'Цена без скидки',
      id: 'yandexMarketBasePrice',
      accessor: _ => _.yandexMarketProducts.map(p => formatPriceOrEmpty(p.basePrice)),
      cellStyle: tableCellStyles.noWrap
    }, {
      title: 'Остаток',
      id: 'yandexMarketBalance',
      accessor: _ => _.yandexMarketProducts.map(p => formatBalance(p.balance)),
      cellStyle: tableCellStyles.noWrap
    }] satisfies GroupedMyColumnsParam<MyYandexMarketProductColumnId, MyProductModel>) // Ensure filtering of ids

export type YandexMarketProductColumnId =
  keyof Pick<YandexMarketProductModel, 'imageUrl' | 'barcodes' | 'name' | 'nameUzb' | 'price' | 'basePrice' | 'balance'
    | 'hasMsProduct' | 'shopSku' | 'vendorCode'>
  | 'category' | 'discountPercentage' | 'createdCardDate' | 'cardPublicationStatus' | 'apiMessage' | 'lastSyncDate'
  | 'logs' | typeof selectColumnId

const createYmMarketCommonProductColumns =
  (ymAccount: YandexMarketAccountModel) => [{
    id: 'imageUrl',
    title: 'Фото',
    CellComponent: ProductImageCell2
  }, {
    id: 'shopSku',
    title: 'Ваш Sku'
  }, {
    id: 'vendorCode',
    title: 'Артикул от производителя'
  }, {
    id: 'barcodes',
    title: 'Штрихкоды',
    cellStyle: tableCellStyles.noWrap
  }, {
    id: 'name',
    title: 'Наименование'
  },
    ymAccount.deliveryType === YandexMarketDeliveryType.UzbFBY
    || ymAccount.deliveryType === YandexMarketDeliveryType.UzbFBS
    ? {
        id: 'nameUzb',
        title: 'Наименование на узбекском'
      }
    : null, {
      id: 'price',
      title: 'Цена',
      accessor: _ => formatPriceOrEmpty(_.price),
      cellStyle: tableCellStyles.noWrap
    }, {
      id: 'basePrice',
      title: 'Цена без скидки',
      accessor: _ => formatPriceOrEmpty(_.basePrice),
      cellStyle: tableCellStyles.noWrap
    }, {
      id: 'balance',
      title: 'Остаток',
      accessor: _ => formatBalance(_.balance),
      cellStyle: tableCellStyles.noWrap
    }, {
      id: 'hasMsProduct',
      title: 'Сматчен с МоимСкладом'
    }] satisfies Array<MyColumnParam<YandexMarketProductColumnId, YandexMarketProductModel> | null>

export const createYmProductColumns =
  (ymAccount: YandexMarketAccountModel) =>
    createColumns<YandexMarketProductColumnId, YandexMarketProductModel>(
      ...createYmMarketCommonProductColumns(ymAccount), {
        id: 'logs',
        title: 'Логи',
        CellComponent: YandexMarketProductSyncLogsCell
      })

export const createYmProductImportColumns =
  (ymAccount: YandexMarketAccountModel) =>
    createColumns<YandexMarketProductColumnId, YandexMarketProductModel>({
      id: selectColumnId,
      title: 'Выбрать',
      CellComponent: TableSelectCell
    }, ...createYmMarketCommonProductColumns(ymAccount))

export type YandexMarketPrintOrderColumnId =
  keyof Pick<YandexMarketPrintOrderModel, 'createdAt' | 'shipmentDate' | 'yandexMarketOrderId' | 'price' | 'productNames'>

export const createYmPrintOrderColumns =
  (ymAccount: YandexMarketAccountModel) =>
    createColumns<YandexMarketPrintOrderColumnId, YandexMarketPrintOrderModel>({
      id: 'createdAt',
      title: 'Дата создания',
      CellComponent: TableDateCell
    }, {
      id: 'shipmentDate',
      title: 'Плановая дата отгрузки',
      accessor: _ => _.shipmentDate ? formatDateOnly(new Date(_.shipmentDate)) : emDash
    }, {
      id: 'yandexMarketOrderId',
      title: 'Id в Yandex.Market'
    }, {
      id: 'price',
      title: `Цена продажи, ${ymAccount.currencySymbol}`,
      accessor: _ => formatPrice(_.price),
      cellStyle: tableCellStyles.noWrap
    }, {
      id: 'productNames',
      title: 'Наименования товаров',
      accessor: _ => _.productNames.join(', ')
    })
